/**
 * The tokens the API is called with, and where they are kept.
 *
 * The stored credential must be the **ID token**: Cognito puts custom attributes in that one
 * only, and `custom:household_id` is what the API scopes every query by. An access token
 * authenticates perfectly well and is then rejected as unauthorised.
 */

const STORAGE_KEY = 'shelf-aware.tokens';

export interface Tokens {
  idToken: string;
  /**
   * The access token, which is what Cognito's own account APIs take.
   *
   * The API never sees this one — it wants the ID token and its household claim. It is here
   * because changing a password is a call to Cognito rather than to us, and `ChangePassword`
   * accepts nothing else.
   */
  accessToken: string;
  /** Long-lived, so the household is not signed out every hour. */
  refreshToken: string;
  /** Epoch milliseconds at which `idToken` stops being accepted. */
  expiresAt: number;
}

export interface Session {
  tokens(): Tokens | null;
  remember(tokens: Tokens): void;
  forget(): void;
}

/** Anything that can produce a currently-valid bearer token. */
export interface Credentials {
  bearer(): Promise<string | null>;
}

/** A session backed by `localStorage`, so a reload does not sign the household out. */
export function browserSession(storage: Storage = localStorage): Session {
  return {
    tokens: () => read(storage),
    remember: (tokens) => storage.setItem(STORAGE_KEY, JSON.stringify(tokens)),
    forget: () => storage.removeItem(STORAGE_KEY),
  };
}

/** A session held in memory, for tests and anything that must not persist a token. */
export function transientSession(initial: Tokens | null = null): Session {
  let held = initial;

  return {
    tokens: () => held,
    remember: (tokens) => {
      held = tokens;
    },
    forget: () => {
      held = null;
    },
  };
}

function read(storage: Storage): Tokens | null {
  const stored = storage.getItem(STORAGE_KEY);
  if (stored === null) {
    return null;
  }

  try {
    return complete(JSON.parse(stored) as Partial<Tokens>);
  } catch {
    // Something else wrote here. Treat it as signed out rather than crashing the app on boot.
    return null;
  }
}

/**
 * The stored blob, if it is one this build can actually use.
 *
 * An older build stored no access token, and a half-usable session is worse than none: it
 * signs in, syncs, and then fails at the one screen that needs the missing half. Asking for a
 * sign-in once is the cheaper end of that trade.
 */
function complete(held: Partial<Tokens>): Tokens | null {
  const { idToken, accessToken, refreshToken, expiresAt } = held;

  if (
    typeof idToken !== 'string' ||
    typeof accessToken !== 'string' ||
    typeof refreshToken !== 'string' ||
    typeof expiresAt !== 'number'
  ) {
    return null;
  }

  return { idToken, accessToken, refreshToken, expiresAt };
}
